/**
 * AllIssuesTab — the super-admin drawer's "All Issues" tab content.
 * SPEC-stakeholder-portal-presentation-redesign-2026-07-20, R3 (shell) / R4 AC3.
 *
 * R3 shipped the tab SHELL + gating + a minimal working stream (every
 * non-epic outstanding work_item + the untriaged queue, via R1's
 * `/api/coordination/outstanding-issues`, reusing `data-table.tsx`). R4 AC3
 * adds the "reuse the internal StatusPill/claim-ladder rendering" content
 * requirement: each row now renders a Status column using the router-derived
 * `claim_ladder` (routers/coordination_view.py::_with_claim_ladder) through
 * the SAME `StatusPill` component the Coordination tab's linked-feedback
 * detail and the client portal use — an internal-fidelity presentation, NOT
 * the client roadmap's distinct hype face (that rule applies only to the
 * Roadmap tab, per this ring's cold-reader context). The tab is super-admin
 * only, backend-enforced at the endpoint's `_require_super_admin` gate — a
 * non-super-admin's call to this data path 403s regardless of any client
 * render attempt (see R1 AC5's endpoint test + this component's own
 * refused-path test).
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { StatusPill } from '@/components/portal/StatusPill';
import { PanelErrorState } from '@/components/portal/panel/PanelErrorState';
import type { CoordinationWorkItem, CoordinationUntriagedFeedback } from '@/lib/schemas';

export const PANEL_ALL_ISSUES_QUERY_KEY = ['stakeholder-portal', 'panel', 'all-issues'] as const;

export function AllIssuesTab() {
  const outstandingQuery = useQuery({
    queryKey: PANEL_ALL_ISSUES_QUERY_KEY,
    queryFn: () => api.coordination.outstandingIssues(),
  });

  if (outstandingQuery.isError) {
    return (
      <PanelErrorState
        what="all outstanding issues"
        onRetry={() => outstandingQuery.refetch()}
        testId="panel-all-issues-error"
      />
    );
  }

  if (outstandingQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8 px-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading all outstanding issues…
      </div>
    );
  }

  const workItems: CoordinationWorkItem[] = outstandingQuery.data?.work_items ?? [];
  const untriaged: CoordinationUntriagedFeedback[] = outstandingQuery.data?.untriaged_feedback ?? [];

  const columns: DataTableColumn<CoordinationWorkItem>[] = [
    {
      key: 'title',
      label: 'Work item',
      wrap: true,
      maxWidth: 'max-w-[220px]',
      render: (w) => (
        <span className="text-sm font-medium">
          {w.title || <span className="text-muted-foreground">(untitled)</span>}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (w) =>
        w.claim_ladder ? (
          <StatusPill rung={w.claim_ladder} testId="all-issues-status" />
        ) : (
          <span className="text-xs text-muted-foreground">{w.stage ?? '—'}</span>
        ),
    },
    {
      key: 'reports',
      label: 'Reports',
      align: 'right',
      render: (w) => <span data-testid="all-issues-feedback-count">{w.feedback_count}</span>,
    },
  ];

  return (
    <div className="p-3 space-y-3" data-testid="panel-all-issues-tab">
      <p className="text-xs text-muted-foreground" data-testid="panel-all-issues-untriaged-count">
        {untriaged.length} untriaged report{untriaged.length === 1 ? '' : 's'} not shown below.
      </p>
      <DataTable<CoordinationWorkItem>
        columns={columns}
        rows={workItems}
        rowKey={(w) => w.id}
        containerTestId="panel-all-issues-table"
        emptyState={<span className="text-muted-foreground">Nothing outstanding.</span>}
      />
    </div>
  );
}
